import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../features/userSlice";
import {
    subscribeVerifiedUsers,
    subscribeUserProfile,
    ensureUserProfile,
    getVerificationType,
    isVerified as checkVerified,
} from "../utils/verified";

const VerifiedContext = createContext(null);

export const VerifiedProvider = ({ children }) => {
    const user = useSelector(selectUser);
    const [verifiedMap, setVerifiedMap] = useState({});
    const [userProfile, setUserProfile] = useState(null);

    useEffect(() => {
        const unsub = subscribeVerifiedUsers(setVerifiedMap);
        return unsub;
    }, []);

    useEffect(() => {
        if (!user?.uid) {
            setUserProfile(null);
            return;
        }
        ensureUserProfile(user);
        const unsub = subscribeUserProfile(user.uid, setUserProfile);
        return unsub;
    }, [user]);

    const isVerified = useCallback(
        (uid, email) => checkVerified(verifiedMap, uid, email),
        [verifiedMap]
    );

    const getBadgeType = useCallback(
        (uid, email) => getVerificationType(verifiedMap, uid, email),
        [verifiedMap]
    );

    const isFounder = useCallback(
        (uid, founderUid) => !!(uid && founderUid && uid === founderUid),
        []
    );

    const isSuperAdmin = userProfile?.role === "SUPER_ADMIN";

    return (
        <VerifiedContext.Provider
            value={{
                verifiedMap,
                userProfile,
                isVerified,
                getBadgeType,
                isFounder,
                isSuperAdmin,
            }}
        >
            {children}
        </VerifiedContext.Provider>
    );
};

export const useVerified = () => {
    const ctx = useContext(VerifiedContext);
    if (!ctx) {
        throw new Error("useVerified must be used within VerifiedProvider");
    }
    return ctx;
};

export default VerifiedContext;
